import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { CashupReportPreview } from '@washday/utilities';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { ICashUpReport } from '../../interfaces/Store';
import { getCashupReportByIdV2, getCashupReportListV2, getPreviewDataForCashClose } from '../../services/cashup.service';
const listQueryKey = 'cashups';

interface IQueryFnParams {
  cashUpReports: ICashUpReport[];
  totalRow: number;
}

export const useGetCashUpsReports = (enabled: boolean, storeId: string, query: {
  limit: number;
  cashierBoxId?: string;
}) => useInfiniteQuery<IQueryFnParams>({
  staleTime: 0,
  enabled: enabled,
  queryKey: [
    listQueryKey,
    storeId,
    query.cashierBoxId || ''
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam: any) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.cashUpReports.length, 0) || 0;
    const lastPageValue = lastPage.totalRow || 0;
    if (lastPageValue <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: { queryKey: any, pageParam: number }) => {
    try {
      const [_key] = queryKey;
      const queryParams = {
        pageNum: pageParam.toString(),
        limit: query.limit?.toString() || '10',
      }
      if (query.cashierBoxId) {
        queryParams.cashierBoxId = query.cashierBoxId;
      }
      const res = await getCashupReportListV2(storeId, queryParams);

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryFnParams;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});


export const useGetCashUpReportById = (storeId: string, id: string) => useQuery<ICashUpReport>({
  queryKey: [listQueryKey, { _id: id }],
  enabled: !!storeId,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, { _id: _id }] = queryKey;
      const res = await getCashupReportByIdV2(storeId, _id);
      if (!res?.data?.data?.cashUpReport) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data.cashUpReport;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});


export const useGetCashUpReportPreviewData = (storeId: string, id: string) => useQuery<CashupReportPreview>({
  queryKey: [listQueryKey, { _id: id }],
  enabled: !!storeId && !!id,
  queryFn: async ({ queryKey }) => {
    try {
      const [_key, { _id: _id }] = queryKey;
      const res = await getPreviewDataForCashClose(storeId, _id);
      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});
